import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  Signal,
  signal,
  viewChild,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ModalComponent } from '@appkit4/angular-components/modal';
import { TranslateService } from '@ngx-translate/core';
import { DrawerSetting, GridColumn } from '@grid/abstractions';
import { GridComponent } from '@shared/modules/grid/components/grid/grid.component';
import { DataType } from '@shared/helpers/enums/data-type';
import { CrudOperation } from '@shared/models';
import { AppNotificationService, HeaderService } from '@shared/services';
import { QueryOptions } from 'odata-query';
import {
  Collection,
  GridItemCreateEvent,
  GridItemUpdateEvent,
} from 'src/app/modules/admin/models';
import { Entity } from 'src/app/modules/portfolio/models/entity';
import { EntityService } from '../../../../core/services/entity/entity.service';
import { ManageEntitiesService } from '../../services/manage-entities.service';
import { UpsertEntityComponent } from '../upsert-entity.component.ts/upsert-entity.component';

@Component({
  standalone: false,
  selector: 'gstar-securities',
  templateUrl: './manage-entities.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageEntitiesComponent implements OnInit, AfterViewInit {
  manageEntitiesService = inject(ManageEntitiesService);
  private translateService = inject(TranslateService);
  private entityService = inject(EntityService);
  private appNotificationService = inject(AppNotificationService);
  private headerService = inject(HeaderService);

  public entities: WritableSignal<Collection<Entity>> = signal({
    total: 0,
    items: [],
  });

  isUpsertFormEnabled: Signal<boolean> = computed(
    () => !!this.headerService.selectedClient()
  );
  @ViewChild('modal', { static: false }) modal: ModalComponent | undefined;

  drawerSetting: DrawerSetting<Entity> = {
    create: {
      title: this.translateService.instant('manageEntities.create'),
      component: UpsertEntityComponent,
      buttons: [
        {
          label: this.translateService.instant('admin.grid.cancel'),
          command: (closeFn): void => {
            closeFn();
          },
          isPrimary: false,
        },
        {
          label: this.translateService.instant('admin.grid.create'),
          command: (closeFn, item): void => {
            if (item?.isValid) {
              this.create({ item: item.model!, onSuccess: closeFn });
            }
          },
          isPrimary: true,
        },
      ],
    },
    edit: {
      title: this.translateService.instant('manageEntities.update'),
      component: UpsertEntityComponent,
      buttons: [
        {
          label: this.translateService.instant('admin.grid.cancel'),
          command: (closeFn): void => {
            closeFn();
          },
          isPrimary: false,
        },
        {
          label: this.translateService.instant('admin.grid.update'),
          command: (closeFn, item): void => {
            if (item.isValid) {
              this.update({
                item: item.model!,
                prevItem: item.prevModel,
                onSuccess: closeFn,
              });
            }
          },
          isPrimary: true,
        },
      ],
    },
  };

  headers: GridColumn[] = [
    {
      label: this.translateService.instant('entity.name'),
      key: 'name',
      sortKey: 'name',
      dataType: DataType.String,
      isSortable: true,
    },
    {
      label: this.translateService.instant('residencyJurisdiction.country'),
      key: 'residencyJurisdiction.country.name',
      sortKey: 'residencyJurisdiction.country.name',
      dataType: DataType.String,
      isSortable: true,
    },
    {
      label: this.translateService.instant('entity.entityType'),
      key: 'entityType.name',
      sortKey: 'entityType.name',
      dataType: DataType.String,
      isSortable: true,
    },
    {
      label: this.translateService.instant('manageEntities.modified'),
      key: 'modified',
      sortKey: 'modified',
      dataType: DataType.Date,
      isSortable: true,
    },
    {
      label: this.translateService.instant('manageEntities.active'),
      key: 'isActive',
      sortKey: 'isActive',
      dataType: DataType.Boolean,
      isSortable: true,
    },
  ];

  destroyRef = inject(DestroyRef);

  selectedClient$ = toObservable(this.headerService.selectedClient);

  gstarGrid = viewChild<GridComponent<Entity, Entity>>('gstarGrid');

  ngAfterViewInit(): void {
    this.selectedClient$.subscribe(() => {
      this.gstarGrid()?.resetPagination();
    });
  }

  ngOnInit(): void {
    this.fetchEntities();
  }

  queryHandler(clientQueryOptions: Partial<QueryOptions<Entity>>): void {
    this.manageEntitiesService.query.set(clientQueryOptions);
  }

  create(entityQuery: GridItemCreateEvent<Entity>): void {
    let isSuccess = false;
    entityQuery.item.clientId = this.headerService.selectedClient()?.id;
    if (this.isUpsertFormEnabled()) {
      this.entityService.create(entityQuery.item).subscribe(() => {
        entityQuery.onSuccess();
        isSuccess = true;
        this.fetchEntities();

        this.appNotificationService.crudNotify(
          CrudOperation.Create,
          entityQuery.item.name ?? '',
          isSuccess,
          false
        );
      });
    } else {
      this.modal?.showModal('modal-no-client');
    }
  }

  update(entityQuery: GridItemUpdateEvent<Entity>): void {
    let isSuccess = false;
    entityQuery.item.clientId = this.headerService.selectedClient()?.id;
    if (!entityQuery.item.clientId) {
      entityQuery.item.clientId = entityQuery.prevItem.clientId;
    }
    this.entityService
      .update(entityQuery.item.entityId ?? 0, entityQuery.item)
      .subscribe(() => {
        entityQuery.onSuccess();
        isSuccess = true;
        this.fetchEntities();

        this.appNotificationService.crudNotify(
          CrudOperation.Update,
          entityQuery.item.name ?? '',
          isSuccess,
          false
        );
      });
  }

  private fetchEntities(): void {
    this.manageEntitiesService.entitiesByClient
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => {
        this.entities.set(response);
      });
  }
}
